import clsx from 'clsx';
import { isEmpty, isUndefined } from 'lodash-es';
import {
  type PropsWithChildren,
  type ReactElement,
  type ReactNode,
} from 'react';

import { ContentGrid } from './components/ContentGrid.js';
import { Label } from './components/Label.js';
import { Trend } from './components/trend/Trend.js';
import type { TrendProps } from './components/trend/types/trend-props.js';
import { Value } from './components/Value.js';
import {
  SingleValueContext,
  type SingleValueContextProps,
} from './contexts/single-value.context.js';
import { getThresholds } from './hooks/get-thresholds.js';
import { FontSizeProvider } from './providers/font-size.provider.js';
import {
  chartStateHandler,
  singleValueContainer,
  singleValueInnerContainer,
  sparklineArea,
  trendArea,
} from './SingleValueRenderer.sty.js';
import type { SingleValueBaseProps } from './types/single-value-base-props.js';
import { buildValueContentGridRows } from './utils/build-value-content-grid-rows.js';
import { formatLabel } from './utils/format-label.js';
import { formatValue } from './utils/format-value.js';
import { defaultChartSize } from '../../../../core/styles/defaultChartValues.sty.js';
import { ChartStateHandler } from '../chart-states/ChartStateHandler.js';
import { LoaderOverlay } from '../loader-overlay/LoaderOverlay.js';
import { DEFAULT_SCALE_FALLBACK_COLOR } from '../sparkline-display/constants.js';
import {
  SparklineDisplay,
  type SparklineDisplayProps,
} from '../sparkline-display/SparklineDisplay.js';
import { getContentAndBackgroundColor } from './utils/get-content-and-background-color.js';

type SingleValueRendererProps = PropsWithChildren<SingleValueBaseProps> & {
  sparklineProps?: SparklineDisplayProps;
  sparklineChildren?: ReactNode;
  trendProps?: TrendProps;
  loading?: boolean;
  emptyState?: ReactNode;
  errorState?: ReactNode | ((errorMessage: string) => ReactElement);
};

export const SingleValueRenderer = (props: SingleValueRendererProps) => {
  const {
    data,
    formatter,
    unit,
    prefixIcon,
    alignment,
    label,
    color,
    thresholds,
    emptyState,
    errorState,
    loading = false,
    sparklineProps,
    sparklineChildren,
    trendProps,
    applyThresholdBackground,
  } = props;

  const calculatedColor = getThresholds(
    data,
    color,
    thresholds,
    DEFAULT_SCALE_FALLBACK_COLOR,
  );

  const { contentColor, backgroundColor } = getContentAndBackgroundColor(
    calculatedColor,
    applyThresholdBackground,
  );

  const { value, unit: formatterUnit } = formatValue(data, unit, formatter);

  const formattedLabel = formatLabel(label);

  const context: SingleValueContextProps = {
    value,
    unit: formatterUnit ?? '',
    label: formattedLabel ?? '',
    prefixIcon,
    alignment: alignment,
  };

  const { innerGrid, outerGrid } = buildValueContentGridRows(
    !isUndefined(label),
    !isUndefined(data),
    !isUndefined(trendProps),
    !isUndefined(sparklineProps),
  );

  const getSparklineColor = () =>
    applyThresholdBackground
      ? contentColor
      : sparklineProps?.color ?? contentColor;

  return (
    <div className={singleValueContainer} style={{ backgroundColor }}>
      <FontSizeProvider
        text={context.prefixIcon ? `00${context.value}` : `${context.value}`}
        suffix={context.unit}
      >
        <SingleValueContext.Provider value={context}>
          <ContentGrid color={contentColor} gridTemplate={outerGrid}>
            {label !== undefined ? (
              <Label
                color={applyThresholdBackground ? contentColor : undefined}
              />
            ) : null}
            <div className={clsx(singleValueInnerContainer)}>
              <ChartStateHandler
                empty={isEmpty(value)}
                height="100cqh"
                emptyState={emptyState}
                errorState={errorState}
                isLoading={loading}
                style={{
                  gridTemplate: innerGrid,
                }}
                className={clsx(defaultChartSize, chartStateHandler)}
                data-testid="chart-state-container"
              >
                <Value />

                {!isUndefined(trendProps) ? (
                  <div className={trendArea}>
                    <Trend {...trendProps} />
                  </div>
                ) : null}

                {!isUndefined(sparklineProps) ? (
                  <div className={sparklineArea}>
                    <SparklineDisplay
                      {...sparklineProps}
                      color={getSparklineColor()}
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'flex-end',
                      }}
                    >
                      {sparklineChildren}
                    </SparklineDisplay>
                  </div>
                ) : null}

                {loading && <LoaderOverlay />}
              </ChartStateHandler>
            </div>
          </ContentGrid>
        </SingleValueContext.Provider>
      </FontSizeProvider>
    </div>
  );
};
